import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import { Body, BodySmallBold } from 'styles/typography';

import { PayerCandidate } from './eligibilityData';
import PayerCandidateCard from './PayerCandidateCard';

// The payer-disambiguation state: PayerSearch returned multiple candidates for the entered
// payer name, and a person must pick the exact payer before the eligibility check can run.
//
// The list only — the selection lives in the slideout, and "Run Eligibility Check" with it, in the
// slideout's own pinned footer. It used to be a sticky bar at the end of this list, which cannot
// reach the bottom of a scrolling body that has padding: sticky positions against the padding box,
// so candidate cards kept scrolling past in the strip underneath it.

interface Props {
  candidates: PayerCandidate[];
  searchTerm?: string | null;
  selectedId: string | null;
  onSelect: (payerId: string) => void;
}

export default function SelectPayerView({ candidates, searchTerm, selectedId, onSelect }: Props) {
  return (
    <Wrapper>
      <ResultsCount>
        {candidates.length} result{candidates.length === 1 ? '' : 's'} found
      </ResultsCount>
      <Explanation>
        Before running an insurance eligibility check, the patient’s payer must be selected from the list of options
        returned{searchTerm ? <> for “{searchTerm}”</> : ''} shown below
      </Explanation>

      <Grid role='radiogroup' aria-label='Payer options'>
        {candidates.map((candidate) => (
          <PayerCandidateCard
            key={candidate.primaryPayerId ?? candidate.stediId ?? candidate.displayName}
            candidate={candidate}
            selected={selectedId === candidate.primaryPayerId}
            onSelect={() => candidate.primaryPayerId && onSelect(candidate.primaryPayerId)}
          />
        ))}
      </Grid>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ResultsCount = styled(BodySmallBold)`
  font-size: 14px;
  color: ${colors.black100};
`;

const Explanation = styled(Body)`
  font-size: 12px;
  color: ${colors.black100};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 8px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;
